"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Clock,
  Heart,
  MonitorPlay,
  Timer,
  Utensils,
} from "lucide-react";
import Link from "next/link";

const PAIRS = [
  {
    icon: Timer,
    slug: "rush-hour-pos",
    title: { en: "Rush-hour POS", es: "POS en hora pico" },
    body: {
      en: "Built to keep up when 18 tables drop checks at the same time. Made for the Friday-night rush.",
      es: "Hecho para seguir el ritmo aunque 18 mesas suelten cuentas al mismo tiempo. Hecho para la cresta del viernes.",
    },
  },
  {
    icon: Utensils,
    slug: "kitchen-display-system",
    title: { en: "Multi-station KDS", es: "KDS multi-estación" },
    body: {
      en: "Apps, grill, bar, dessert — each station gets only what they need. Course firing keeps tables tight.",
      es: "Apps, plancha, bar, postre — cada estación recibe solo lo suyo. Course firing mantiene mesas sincronizadas.",
    },
  },
  {
    icon: Heart,
    slug: "loyalty-crm",
    title: { en: "Loyalty CRM", es: "Loyalty CRM" },
    body: {
      en: "Maya remembered the Hendersons drink Negroni 1st. Now your POS does too — every host and every server.",
      es: "Maya recordaba que los Henderson empiezan con Negroni. Ahora tu POS también — para todo host y mesero.",
    },
  },
  {
    icon: Clock,
    slug: "employee-time-clock",
    title: { en: "Time clock + tip pool", es: "Time clock + propinas" },
    body: {
      en: "Servers clock in, tips split by hours or sales, payroll exports clean. No spreadsheet Sundays.",
      es: "Los meseros checan, las propinas se reparten por horas o ventas, payroll exporta limpio. Sin domingos de hoja de cálculo.",
    },
  },
  {
    icon: MonitorPlay,
    slug: "live-menu-boards",
    title: { en: "Live menu boards", es: "Menus en vivo" },
    body: {
      en: "Change the daily special on the iPad, watch every TV behind the bar update in seconds. No reprinting.",
      es: "Cambia el especial del día en el iPad, mira cada TV detrás del bar actualizarse en segundos. Sin reimprimir.",
    },
  },
];

export default function RestaurantsPairs() {
  const { t, lang } = useLang();

  return (
    <section className="py-24 px-6 bg-redy-warm-white">
      <div className="max-w-[1140px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            {t("Pairs well with", "Combina con")}
          </div>
          <h2 className="text-[36px] md:text-[52px] font-black leading-[1.04] tracking-tight text-redy-charcoal max-w-[760px] mx-auto">
            {t(
              "Everything dine-in restaurants actually need.",
              "Todo lo que un restaurante dine-in realmente necesita."
            )}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PAIRS.map((p, i) => {
            const Icon = p.icon;
            const isWide = i === 4;
            return (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                className={isWide ? "sm:col-span-2 lg:col-span-1" : ""}
              >
                <Link
                  href={`/features/${p.slug}`}
                  className="group block h-full bg-white border border-black/[0.04] rounded-3xl p-7 hover:shadow-[0_20px_40px_-20px_rgba(0,0,0,0.12)] transition-all hover:-translate-y-0.5"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-11 h-11 bg-redy-charcoal rounded-xl flex items-center justify-center group-hover:bg-redy-orange transition-colors">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-redy-charcoal/30 group-hover:text-redy-orange group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <h3 className="text-lg font-black text-redy-charcoal mb-2">
                    {p.title[lang as "en" | "es"] ?? p.title.en}
                  </h3>
                  <p className="text-sm text-redy-secondary leading-relaxed">
                    {p.body[lang as "en" | "es"] ?? p.body.en}
                  </p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
